import * as Y from 'yjs';
import type { Edge, Node, Scene } from '../../core/models/scene';
import { ShapeMapAdapter } from './ShapeMapAdapter';
import { ShapePersistenceGateway } from './shapeGeometry';

export const RDM_MANAGED_ATTR = '__rdmManaged';

const STRUCTURAL_NODE_KEYS = new Set([
  'id',
  'type',
  'category',
  'x',
  'y',
  'w',
  'h',
  'width',
  'height',
  'zIndex',
  'style',
  'props',
]);

function toShapeRecord(node: Node): Record<string, unknown> {
  const props =
    node.props && typeof node.props === 'object'
      ? { ...(node.props as Record<string, unknown>) }
      : {};

  Object.entries(node as unknown as Record<string, unknown>).forEach(([key, value]) => {
    if (!STRUCTURAL_NODE_KEYS.has(key)) {
      props[key] = value;
    }
  });

  if (node.attrs && typeof node.attrs === 'object') {
    props.attrs = { ...(node.attrs as Record<string, unknown>) };
  }

  return {
    ...node,
    width: node.width ?? node.w ?? 80,
    height: node.height ?? node.h ?? 80,
    zIndex: node.zIndex ?? 0,
    ...(node.attrs?.rotation !== undefined ? { rotation: node.attrs.rotation } : {}),
    props,
    style: {
      fillColor: node.fillColor,
      strokeColor: node.strokeColor,
      strokeWidth: node.strokeWidth,
    },
  };
}

function toConnectorRecord(edge: Edge): Record<string, unknown> {
  const routingType = edge.attrs?.routingType as
    | 'straight'
    | 'orthogonal'
    | 'orthogonalRounded'
    | 'bezier'
    | 'rounded'
    | 'manual'
    | undefined;

  return {
    id: edge.id,
    type: edge.type,
    sourceShapeId: edge.from,
    targetShapeId: edge.to,
    ...(routingType ? { routingType } : {}),
    ...(edge.fromAnchor ? { sourceAnchor: edge.fromAnchor, fromAnchor: edge.fromAnchor } : {}),
    ...(edge.toAnchor ? { targetAnchor: edge.toAnchor, toAnchor: edge.toAnchor } : {}),
    ...(edge.fromOffset !== undefined ? { sourceOffset: edge.fromOffset, fromOffset: edge.fromOffset } : {}),
    ...(edge.toOffset !== undefined ? { targetOffset: edge.toOffset, toOffset: edge.toOffset } : {}),
    ...(edge.fromAnchorLocked !== undefined ? { fromAnchorLocked: edge.fromAnchorLocked } : {}),
    ...(edge.toAnchorLocked !== undefined ? { toAnchorLocked: edge.toAnchorLocked } : {}),
    props: {
      attrs: {
        ...(edge.attrs || {}),
      },
      ...(edge.fromAnchor ? { fromAnchor: edge.fromAnchor } : {}),
      ...(edge.toAnchor ? { toAnchor: edge.toAnchor } : {}),
      ...(edge.fromOffset !== undefined ? { fromOffset: edge.fromOffset } : {}),
      ...(edge.toOffset !== undefined ? { toOffset: edge.toOffset } : {}),
      ...(edge.fromAnchorLocked !== undefined ? { fromAnchorLocked: edge.fromAnchorLocked } : {}),
      ...(edge.toAnchorLocked !== undefined ? { toAnchorLocked: edge.toAnchorLocked } : {}),
    },
  };
}

function readAttrs(record: unknown): Record<string, unknown> | undefined {
  if (!record || typeof record !== 'object') return undefined;

  const objectRecord = record as Record<string, unknown>;
  if (objectRecord.attrs && typeof objectRecord.attrs === 'object') {
    return objectRecord.attrs as Record<string, unknown>;
  }

  const props = objectRecord.props;
  if (!props || typeof props !== 'object') return undefined;

  const attrs = (props as Record<string, unknown>).attrs;
  return attrs && typeof attrs === 'object'
    ? (attrs as Record<string, unknown>)
    : undefined;
}

function isRdmManagedRecord(record: unknown): boolean {
  return readAttrs(record)?.[RDM_MANAGED_ATTR] === true;
}

function markNodeAsRdmManaged(node: Node): Node {
  return {
    ...node,
    attrs: {
      ...(node.attrs ?? {}),
      [RDM_MANAGED_ATTR]: true,
    },
  };
}

function markEdgeAsRdmManaged(edge: Edge): Edge {
  return {
    ...edge,
    attrs: {
      ...(edge.attrs ?? {}),
      [RDM_MANAGED_ATTR]: true,
    },
  };
}

function prepareRdmScene(scene: Pick<Scene, 'nodes' | 'edges'>): Pick<Scene, 'nodes' | 'edges'> {
  return {
    nodes: scene.nodes.map(markNodeAsRdmManaged),
    edges: scene.edges.map(markEdgeAsRdmManaged),
  };
}

export function replaceSceneInYDoc(
  doc: Y.Doc,
  scene: Pick<Scene, 'nodes' | 'edges'>
): void {
  const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const connectorsMap = doc.getMap('connectors');
  const shapeRecords = scene.nodes.map(toShapeRecord);
  shapes.validateCreates(shapeRecords);

  doc.transact(() => {
    shapes.clear();
    connectorsMap.clear();

    shapeRecords.forEach((shape) => shapes.create(shape));

    scene.edges.forEach((edge) => {
      connectorsMap.set(edge.id, toConnectorRecord(edge));
    });
  }, 'rdm-import');
}

export function mergeRdmScene(
  currentScene: Pick<Scene, 'nodes' | 'edges'>,
  nextScene: Pick<Scene, 'nodes' | 'edges'>
): Pick<Scene, 'nodes' | 'edges'> {
  const rdmScene = prepareRdmScene(nextScene);
  const nextNodeIds = new Set(rdmScene.nodes.map((node) => node.id));
  const nextEdgeIds = new Set(rdmScene.edges.map((edge) => edge.id));

  return {
    nodes: [
      ...currentScene.nodes.filter((node) => (
        !nextNodeIds.has(node.id) && !isRdmManagedRecord(node)
      )),
      ...rdmScene.nodes,
    ],
    edges: [
      ...currentScene.edges.filter((edge) => (
        !nextEdgeIds.has(edge.id) && !isRdmManagedRecord(edge)
      )),
      ...rdmScene.edges,
    ],
  };
}

export function applyRdmSceneInYDoc(
  doc: Y.Doc,
  scene: Pick<Scene, 'nodes' | 'edges'>
): void {
  const rdmScene = prepareRdmScene(scene);
  const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(doc.getMap('shapes')));
  const connectorsMap = doc.getMap('connectors');
  const nextNodeIds = new Set(rdmScene.nodes.map((node) => node.id));
  const nextEdgeIds = new Set(rdmScene.edges.map((edge) => edge.id));
  const shapeRecords = rdmScene.nodes.map(toShapeRecord);
  shapes.validateCreates(shapeRecords);

  doc.transact(() => {
    shapes.forEach((shape, id) => {
      if (isRdmManagedRecord(shape) && !nextNodeIds.has(id)) {
        shapes.delete(id);
      }
    });

    connectorsMap.forEach((connector, id) => {
      if (isRdmManagedRecord(connector) && !nextEdgeIds.has(id)) {
        connectorsMap.delete(id);
      }
    });

    shapeRecords.forEach((shape) => {
      shapes.upsert(shape);
    });

    rdmScene.edges.forEach((edge) => {
      connectorsMap.set(edge.id, toConnectorRecord(edge));
    });
  }, 'rdm-apply');
}

export function replaceSceneInWorkspace(options: {
  doc: Y.Doc | null;
  nodes: Node[];
  edges: Edge[];
  loadScene: (scene: Scene) => void;
  validateFinalScene?: (scene: Pick<Scene, 'nodes' | 'edges'>) => boolean;
}): void {
  const { doc, nodes, edges, loadScene, validateFinalScene } = options;
  if (validateFinalScene && !validateFinalScene({ nodes, edges })) return;

  if (doc) {
    replaceSceneInYDoc(doc, { nodes, edges });
    return;
  }

  loadScene({
    version: 1,
    nodes,
    edges,
  });
}

export function applyRdmSceneInWorkspace(options: {
  doc: Y.Doc | null;
  nodes: Node[];
  edges: Edge[];
  currentScene: Pick<Scene, 'nodes' | 'edges'>;
  loadScene: (scene: Scene) => void;
  validateFinalScene?: (scene: Pick<Scene, 'nodes' | 'edges'>) => boolean;
}): void {
  const { doc, nodes, edges, currentScene, loadScene, validateFinalScene } = options;
  const mergedScene = mergeRdmScene(currentScene, { nodes, edges });
  if (validateFinalScene && !validateFinalScene(mergedScene)) return;

  if (doc) {
    applyRdmSceneInYDoc(doc, { nodes, edges });
    return;
  }

  loadScene({
    version: 1,
    ...mergedScene,
  });
}
